/**
 * Magic Link Request Component
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 * Formulaire de demande de lien magique pour connexion passwordless
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Send,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { magicLinkApi } from '../api/magic-link.api';

export interface MagicLinkRequestProps {
  /** Callback pour retourner au login classique */
  onBack: () => void;
  /** Email pré-rempli (optionnel) */
  defaultEmail?: string;
  /** Classe CSS additionnelle */
  className?: string;
}

type RequestState = 'form' | 'sent' | 'error';

export const MagicLinkRequest: React.FC<MagicLinkRequestProps> = ({
  onBack,
  defaultEmail = '',
  className = '',
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail);
  const [state, setState] = useState<RequestState>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail || isLoading || cooldown > 0) return;

    try {
      setIsLoading(true);
      setError(null);

      await magicLinkApi.requestMagicLink(email);
      setState('sent');

      // Start cooldown for resend (60 seconds)
      setCooldown(60);
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setError(err.message || t('auth.magicLink.error'));
      setState('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || isLoading) return;

    try {
      setIsLoading(true);
      setError(null);

      await magicLinkApi.resendMagicLink(email);

      // Reset cooldown
      setCooldown(60);
      const interval = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setError(err.message || t('auth.magicLink.resendError'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = `w-full px-4 py-4 text-base bg-white/80 backdrop-blur-sm border-2 rounded-2xl
    transition-all duration-300 outline-none
    focus:border-teal-500 focus:bg-white focus:shadow-lg focus:shadow-teal-500/10
    border-gray-200 hover:border-teal-300
    placeholder:text-gray-400`;

  // Sent state - show success message
  if (state === 'sent') {
    return (
      <div className={`w-full max-w-md ${className}`}>
        <div className="text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {t('auth.magicLink.sentTitle')}
          </h2>

          <p className="text-gray-600 mb-2">
            {t('auth.magicLink.sentDescription')}
          </p>
          <p className="text-teal-700 font-semibold text-lg mb-6">
            {email}
          </p>

          {/* Instructions */}
          <div className="bg-teal-50 border border-teal-100 rounded-2xl p-5 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-teal-700">
                <p className="font-medium mb-1">{t('auth.magicLink.expiresIn')}</p>
                <p className="text-teal-600">
                  {t('auth.magicLink.clickInstruction')}
                </p>
              </div>
            </div>
          </div>

          {/* Resend Button */}
          <button
            onClick={handleResend}
            disabled={cooldown > 0 || isLoading}
            className="w-full py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl
              hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50/50
              transition-all duration-300 flex items-center justify-center gap-2
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t('auth.magicLink.sending')}</span>
              </>
            ) : cooldown > 0 ? (
              <>
                <Clock className="w-5 h-5" />
                <span>{t('auth.magicLink.resendIn', { seconds: cooldown })}</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                <span>{t('auth.magicLink.resend')}</span>
              </>
            )}
          </button>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Back to Login */}
          <button
            onClick={onBack}
            className="w-full mt-4 py-4 text-gray-500 hover:text-teal-600 font-medium
              transition-colors duration-300 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.magicLink.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // Form state - show email input
  return (
    <div className={`w-full max-w-md ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('auth.magicLink.title')}
        </h2>
        <p className="text-gray-500">
          {t('auth.magicLink.subtitle')}
        </p>
      </div>

      {/* Error Message */}
      {(state === 'error' || error) && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-600 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error || t('auth.magicLink.error')}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t('auth.fields.emailAddress')}
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <Mail size={20} />
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
                if (state === 'error') setState('form');
              }}
              className={`${inputClasses} pl-12`}
              placeholder={t('auth.fields.emailPlaceholder')}
              autoFocus
              autoComplete="email"
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isValidEmail || isLoading}
          className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800
            text-white font-bold text-base rounded-2xl transition-all duration-300
            disabled:opacity-50 shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40
            flex items-center justify-center gap-3"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{t('auth.magicLink.sending')}</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>{t('auth.magicLink.sendLink')}</span>
            </>
          )}
        </button>
      </form>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-gray-50 border border-gray-100 rounded-2xl">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">{t('auth.magicLink.howItWorks')}</p>
            <p className="text-gray-500 text-xs">
              {t('auth.magicLink.howItWorksDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Back to Login */}
      <button
        onClick={onBack}
        className="w-full mt-6 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl
          hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50/50
          transition-all duration-300 flex items-center justify-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('auth.magicLink.backToClassicLogin')}
      </button>
    </div>
  );
};

export default MagicLinkRequest;
